import { getSchemaAlternatives } from '../Services/JsonSchemaTypes';
import { PrimitiveType } from '../../../api/api';
import { ObjectPropertyType } from './ObjectPropertyType';
import { ObjectSchemaType } from './ObjectSchemaType';

function getSchemaTypeName(schema: NonNullable<ObjectPropertyType['jsonSchema']>): string {
	const alternatives = getSchemaAlternatives(schema);
	if (schema.title && alternatives.every(branch => branch.type !== 'null' && branch.title === schema.title)) return schema.title;
	if (alternatives.length > 1) {
		return Array.from(new Set(alternatives.map(getSchemaTypeName))).join(' | ');
	}

	const [branch] = alternatives;
	if (branch.type === 'null') return 'null';
	if (branch.enum?.length) return branch.enum.map(value => JSON.stringify(value)).join(' | ');
	if (branch.type === 'object' && branch.additionalProperties && typeof branch.additionalProperties === 'object') {
		return `Record<String, ${getSchemaTypeName(branch.additionalProperties as NonNullable<ObjectPropertyType['jsonSchema']>)}>`;
	}
	if (branch.type === 'array') {
		const itemSchema = branch.items && !Array.isArray(branch.items) ? branch.items : undefined;
		if (!itemSchema) return 'Any[]';
		const itemType = getSchemaTypeName(itemSchema);
		return `${getSchemaAlternatives(itemSchema).length > 1 ? `(${itemType})` : itemType}[]`;
	}
	const formattedType =
		branch.format === 'uuid' ? 'Guid' :
			branch.format === 'date-time' ? 'DateTime' :
				branch.format === 'date' ? 'Date' :
					branch.format === 'duration' ? 'TimeSpan' : undefined;
	const primitiveNames: Record<string, string> = {
		string: PrimitiveType.String,
		boolean: PrimitiveType.Boolean,
		integer: PrimitiveType.Integer,
		number: PrimitiveType.Decimal,
		object: PrimitiveType.Object,
	};
	return formattedType ?? branch.title ?? primitiveNames[String(branch.type)] ?? 'Any';
}

// Helper function to get the display name of a property type, including handling for collections and nested collections
export default function getPropertyTypeName(property: ObjectPropertyType): string {
	if (property.jsonSchema?.anyOf || property.jsonSchema?.oneOf || property.jsonSchema?.enum || Array.isArray(property.jsonSchema?.type)) {
		return getSchemaTypeName(property.jsonSchema!);
	}
	const dictionaryValueType = (property.referenceEntity as ObjectSchemaType | undefined)?.additionalProperties;
	const baseName = dictionaryValueType
		? `Record<String, ${getPropertyTypeName(dictionaryValueType)}>`
		: property.referenceEnum
			? property.referenceEnum.name
			: property.referenceEntity
				? property.referenceEntity.name
				: property.type;

	if (property.isMultiple && property.isMultipleNested) {
		return `${baseName}[][]`;
	}
	if (property.isMultiple) {
		return `${baseName}[]`;
	}
	return baseName;
}
